import Query from './query'
import semver from 'semver'
import { RPC_VERSION, DEFAULT_CLI, QURAS_NETWORK } from '../consts'
import logger from '../logging'

const log = logger('rpc')

/**
 * @class CliClient
 * @classdesc
 * Cli Client model to query a Quras node. Contains built-in methods to query using Cli calls.
 * @param {string} net - 'MainNet' or 'TestNet' will query the default Cli address found in consts. You may provide a custom URL.
 * @param {string} version - Version of QURAS node. Used to check if Cli methods have been implemented. it will default to DEFAULT_Cli found in CONST
 */
class CliClient {
  constructor (net, version = RPC_VERSION) {
    if (net === QURAS_NETWORK.MAIN) {
      this.net = DEFAULT_CLI.MAIN
    } else if (net === QURAS_NETWORK.TEST) {
      this.net = DEFAULT_CLI.TEST
    } else if (net === QURAS_NETWORK.DEV) {
      this.net = DEFAULT_CLI.DEV
    } else {
      /**
       * The URL of the node that this client queries.
       * @type string
       */
      this.net = net
    }

    /**
     * Version of this client. Used to check if RPC call is implemented.
     * @type string
     */
    this.version = ''
    if (semver.valid(version)) {
      this.version = semver.clean(version)
    } else {
      throw new Error(`Invalid Version: ${version}`)
    }
  }

  /**
   * Takes an Query object and executes it. Adds the Query object to history.
   * @param {Query} query
   * @param {AxiosRequestConfig} config
   * @return {Promise<any>}
   */
  execute (query, config) {
    log.info(`RPC: ${this.net} executing Query[${query.req.method}]`)
    return query.execute(this.net, config)
  }

  /**
   * Creates a query with the given req and immediately executes it.
   * @param {object} req
   * @param {AxiosRequestConfig} config
   * @return {Promise<any>}
   */
  query (req, config) {
    const query = new Query(req)
    return this.execute(query, config)
  }

  /**
   * Get the server's one-time pubkey.
   * @return {Promise<any>}
   */
  getOneTimePubkey () {
    return this.execute(Query.getOneTimePubkey())
      .then((res) => {
        if (res.result === undefined) return ''
        return res.result.public_key
      })
  }

  /**
   * Create anonymous wallet.
   * @param {string} spendingKey - client's spending key
   * @return {Promise<any>}
   */
  createAnonymousWallet (spendingKey) {
    var params = []
    params.push(spendingKey.server_pubkey)
    params.push(spendingKey.client_pubkey)
    params.push(spendingKey.seed_key)
    params.push(spendingKey.encrypted)
    return this.execute(Query.createAnonymousWallet(params))
      .then((res) => {
        if (res.result === undefined) return ''
        return res.result
      })
  }

  /**
   * Create anonymous wallet.
   * @param {string} viewingPriKey - client's viewing prikey
   * @return {Promise<any>}
   */
  createStealthWallet (viewingPriKey) {
    var params = []
    params.push(viewingPriKey.server_pubkey)
    params.push(viewingPriKey.client_pubkey)
    params.push(viewingPriKey.seed_key)
    params.push(viewingPriKey.encrypted)
    return this.execute(Query.createStealthWallet(params))
      .then((res) => {
        if (res.result === undefined) return ''
        return res.result
      })
  }

  /**
   * Get Balance.
   * @param {string} walletData - walletData
   * @return {Promise<any>}
   */
  getPrivateBalance (walletData) {
    var params = []
    params.push(walletData.server_pubkey)
    params.push(walletData.client_pubkey)
    params.push(walletData.seed_key)
    params.push(walletData.encrypted)
    return this.execute(Query.getPrivateBalance(params))
      .then((res) => {
        if (res.result === undefined) return ''
        return res.result
      })
  }

  /**
   * Get Balance.
   * @param {string} walletData - walletData
   * @return {Promise<any>}
   */
  getPrivateTransactionHistory (walletData) {
    var params = []
    params.push(walletData.server_pubkey)
    params.push(walletData.client_pubkey)
    params.push(walletData.seed_key)
    params.push(walletData.encrypted)
    return this.execute(Query.getPrivateTransactionHistory(params))
      .then((res) => {
        if (res.result === undefined) return ''
        return res.result
      })
  }

  /**
   * Send Private Tx.
   * @param {string} txData - walletData
   * @return {Promise<any>}
   */
  sendPrivateTx (txData) {
    var params = []
    params.push(txData.server_pubkey)
    params.push(txData.client_pubkey)
    params.push(txData.seed_key)
    params.push(txData.encrypted)
    return this.execute(Query.sendPrivateTx(params))
      .then((res) => {
        if (res.result === undefined) return ''
        return res.result
      })
  }

  /**
   * Rebuild wallet.
   * @param {string} walletData - walletData
   * @return {Promise<any>}
   */
  rebuildWallet (walletData) {
    var params = []
    params.push(walletData.server_pubkey)
    params.push(walletData.client_pubkey)
    params.push(walletData.seed_key)
    params.push(walletData.encrypted)
    return this.execute(Query.rebuildWallet(params))
      .then((res) => {
        if (res.result === undefined) return ''
        return res.result
      })
  }
}

export default CliClient
